// src/services/fusionPtrService.js
import api from '../api/apiClient';

export const fusionPtrService = {
  // List accessible analyses (archived filtered client-side)
  async getAnalyses(page = 1, limit = 100) {
    return api.get('/analyses', { page: page.toString(), limit: limit.toString() });
  },

  // Stateless holistic snapshot for 1..10 analyses
  async collect(analysisIds) {
    return api.post('/ptr-fusions/collect', { analysisIds });
  },

  // Collect + persist a draft fusion (internal ids + library externals + inline snapshots)
  async createFusion(name, analysisIds, externalPtrIds = [], externalSnapshots = []) {
    return api.post('/ptr-fusions', { name, analysisIds, externalPtrIds, externalSnapshots });
  },

  // AI extraction of an exterior PTR file (stateless — returns a snapshot for review)
  async importExternalPtr(file, language = 'fr') {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', language);
    return api.upload('/ptr-fusions/import-external', formData);
  },

  // Exterior PTR library (persisted, company-scoped)
  async getExternalPtrs() {
    return api.get('/external-ptrs');
  },

  async getExternalPtr(id) {
    return api.get(`/external-ptrs/${id}`);
  },

  async saveExternalPtr(snapshot) {
    return api.post('/external-ptrs', { snapshot });
  },

  async updateExternalPtr(id, snapshot) {
    return api.patch(`/external-ptrs/${id}`, { snapshot });
  },

  async deleteExternalPtr(id) {
    return api.delete(`/external-ptrs/${id}`);
  },

  // Attach a library exterior PTR (or inline reviewed snapshot) to a draft fusion
  async addExternalSource(id, snapshot) {
    return api.post(`/ptr-fusions/${id}/add-external-source`, { snapshot });
  },

  async addExternalPtrId(id, externalPtrId) {
    return api.post(`/ptr-fusions/${id}/add-external-source`, { externalPtrId });
  },

  async getFusions() {
    return api.get('/ptr-fusions');
  },

  async getFusion(id) {
    return api.get(`/ptr-fusions/${id}`);
  },

  async updateFusion(id, payload) {
    return api.patch(`/ptr-fusions/${id}`, payload);
  },

  // Re-collect frozen snapshots in place (draft only) — proposals stay valid
  async refreshFusion(id) {
    const response = await api.post(`/ptr-fusions/${id}/refresh`, {});
    return response.data;
  },

  async deleteFusion(id) {
    return api.delete(`/ptr-fusions/${id}`);
  },

  // AI fusion proposals (fuse / cover / conflict) for a saved fusion
  async suggestFusion(fusionId, language = 'fr') {
    return api.post('/ai/suggest-ptr-fusion', { fusionId, language });
  },

  // Export a validated fusion (xlsx | json, in fr | en) — triggers a file download
  async exportFusion(id, format = 'xlsx', lang = 'fr') {
    const response = await api.get(`/ptr-fusions/${id}/export`, { format, lang });
    const payload = response.data || {};
    if (format === 'json') {
      downloadFile(`ptr-fusion-${id}.json`, JSON.stringify(payload, null, 2), 'application/json');
      return payload;
    }
    downloadBase64File(
      payload.filename || `ptr-fusion-${id}.xlsx`,
      payload.content,
      payload.mimeType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    );
    return payload;
  },

  // Client-side PDF report (jsPDF, Workshop 1 layout) — works on drafts and
  // validated fusions alike, no server round-trip.
  async exportPdf(fusion, finalMeasures, lang = 'fr') {
    const { default: FusionPtrPdfGenerator } = await import('../utils/fusionPtrPdfGenerator');
    const generator = new FusionPtrPdfGenerator(fusion, finalMeasures, lang);
    await generator.generateFusionPtrReport();
    const safeName = String(fusion.name || fusion._id || 'fusion').replace(/[^\w\-àâäéèêëîïôöùûüç]+/gi, '-').slice(0, 60);
    generator.save(`ptr-fusion-${safeName}-${lang === 'en' ? 'EN' : 'FR'}.pdf`);
    return true;
  },
};

const downloadBase64File = (filename, base64Content, mime) => {
  const binaryString = window.atob(base64Content);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

const downloadFile = (filename, content, mime) => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};
